import React, { createContext, useContext, useEffect } from 'react';
import { useTheme } from './ThemeContext';

const VariantContext = createContext();

export const useVariant = () => {
  const context = useContext(VariantContext);
  if (!context) {
    throw new Error('useVariant must be used within a VariantProvider');
  }
  return context;
};

export const VariantProvider = ({ children }) => {
  const { domainConfig } = useTheme();
  const currentVariant = domainConfig?.variant || 'corporate';

  // Apply variant styles to CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    const styles = getVariantStyles();
    
    // Set CSS custom properties for variant colors
    root.style.setProperty('--variant-primary', getVariantColor(currentVariant, 'primary'));
    root.style.setProperty('--variant-secondary', getVariantColor(currentVariant, 'secondary'));
    root.style.setProperty('--variant-accent', getVariantColor(currentVariant, 'accent'));
    root.style.setProperty('--variant-background', getVariantColor(currentVariant, 'background'));
    root.style.setProperty('--variant-text', getVariantColor(currentVariant, 'text'));
    root.style.setProperty('--variant-text-secondary', getVariantColor(currentVariant, 'textSecondary'));
  }, [currentVariant]);

  // Get variant-specific colors
  const getVariantColor = (variant, colorType) => {
    const colorMap = {
      minimal: {
        primary: '#374151',
        secondary: '#6B7280',
        accent: '#9CA3AF',
        background: '#FFFFFF',
        text: '#111827',
        textSecondary: '#6B7280'
      },
      modern: {
        primary: '#8B5CF6',
        secondary: '#EC4899',
        accent: '#C084FC',
        background: '#1E293B',
        text: '#FFFFFF',
        textSecondary: '#E2E8F0'
      },
      corporate: {
        primary: '#2563EB',
        secondary: '#64748B',
        accent: '#3B82F6',
        background: '#FFFFFF',
        text: '#111827',
        textSecondary: '#6B7280'
      },
      creative: {
        primary: '#F97316',
        secondary: '#EC4899',
        accent: '#FB923C',
        background: '#FEF3C7',
        text: '#111827',
        textSecondary: '#374151'
      },
      grid: {
        primary: '#2563EB',
        secondary: '#64748B',
        accent: '#3B82F6',
        background: '#F9FAFB',
        text: '#111827',
        textSecondary: '#6B7280'
      },
      magazine: {
        primary: '#1F2937',
        secondary: '#6B7280',
        accent: '#374151',
        background: '#FFFFFF',
        text: '#111827',
        textSecondary: '#6B7280'
      },
      professional: {
        primary: '#2563EB',
        secondary: '#64748B',
        accent: '#3B82F6',
        background: '#F8FAFC',
        text: '#0F172A',
        textSecondary: '#64748B'
      },
      clean: {
        primary: '#1F2937',
        secondary: '#6B7280',
        accent: '#9CA3AF',
        background: '#FFFFFF',
        text: '#111827',
        textSecondary: '#6B7280'
      },
      landing: {
        primary: '#FFFFFF',
        secondary: '#E2E8F0',
        accent: '#CBD5E1',
        background: '#2563EB',
        text: '#FFFFFF',
        textSecondary: '#E2E8F0'
      },
      elegant: {
        primary: '#1E293B',
        secondary: '#64748B',
        accent: '#94A3B8',
        background: '#F8FAFC',
        text: '#0F172A',
        textSecondary: '#64748B'
      },
      dark: {
        primary: '#3B82F6',
        secondary: '#8B5CF6',
        accent: '#A78BFA',
        background: '#111827',
        text: '#FFFFFF',
        textSecondary: '#D1D5DB'
      }
    };
    
    return colorMap[variant]?.[colorType] || colorMap.corporate[colorType];
  };

  // Define variant-specific styling classes and configurations
  const getVariantStyles = () => {
    switch (currentVariant) {
      case 'minimal':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-gray-400',
          section: 'bg-gray-50',
          hero: 'bg-white',
          footer: 'bg-white border-t border-gray-200',
          navbar: 'bg-white border-b border-gray-200',
          input: 'border border-gray-300 focus:border-gray-500',
          link: 'text-gray-600 hover:text-gray-900'
        };
      case 'modern':
        return {
          container: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10',
          button: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white',
          buttonSecondary: 'border-2 border-white/30 text-white hover:bg-white/10 hover:text-slate-900',
          text: 'text-white',
          textSecondary: 'text-purple-100',
          accent: 'text-purple-200',
          section: 'bg-gray-800/50',
          hero: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
          footer: 'bg-gradient-to-r from-slate-900 to-purple-900 text-white',
          footerText: 'text-white',
          footerTextSecondary: 'text-purple-100',
          footerAccent: 'text-purple-200',
          footerLink: 'text-purple-200 hover:text-white',
          navbar: 'bg-slate-900/95 backdrop-blur-md border-b border-white/10',
          input: 'bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-purple-400',
          link: 'text-purple-200 hover:text-white'
        };
      case 'corporate':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-blue-600',
          section: 'bg-gray-50',
          hero: 'bg-white',
          footer: 'bg-blue-900 text-white',
          footerText: 'text-white',
          footerTextSecondary: 'text-blue-100',
          footerAccent: 'text-blue-200',
          footerLink: 'text-blue-100 hover:text-white',
          navbar: 'bg-white border-b border-gray-200',
          input: 'border border-gray-300 focus:border-blue-500',
          link: 'text-blue-600 hover:text-blue-700'
        };
      case 'creative':
        return {
          container: 'bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100',
          card: 'bg-white/80 backdrop-blur-sm border border-white/50 shadow-lg',
          button: 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600',
          buttonSecondary: 'border-4 border-purple-400 text-purple-600 hover:bg-purple-400 hover:text-white',
          text: 'text-gray-900',
          textSecondary: 'text-gray-700',
          accent: 'text-orange-500',
          section: 'bg-white/50',
          hero: 'bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100',
          footer: 'bg-gradient-to-r from-orange-600 to-purple-600 text-white',
          navbar: 'bg-white/80 backdrop-blur-sm border-b border-white/50',
          input: 'border border-orange-300 focus:border-orange-500',
          link: 'text-orange-600 hover:text-orange-700'
        };
      case 'grid':
        return {
          container: 'bg-gradient-to-br from-blue-50 to-white',
          card: 'bg-white border shadow-sm',
          button: 'bg-[#195A8E] text-white hover:bg-[#144A73] hover:opacity-90',
          buttonSecondary: 'border border-[#195A8E] text-[#195A8E] hover:bg-[#195A8E] hover:text-white',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-[#195A8E]',
          section: 'bg-gradient-to-br from-blue-50 to-white',
          hero: 'bg-gradient-to-br from-blue-50 to-white',
          footer: 'text-white',
          footerText: 'text-white',
          footerTextSecondary: 'text-blue-100',
          footerAccent: 'text-blue-200',
          footerLink: 'text-blue-100 hover:text-white',
          navbar: 'border-b',
          input: 'border focus:border-[#195A8E]',
          link: 'text-[#195A8E] hover:opacity-80'
        };
      case 'magazine':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-gray-800',
          section: 'bg-gray-50',
          hero: 'bg-white',
          footer: 'bg-gray-800 text-white',
          footerText: 'text-white',
          footerTextSecondary: 'text-gray-300',
          footerAccent: 'text-gray-200',
          footerLink: 'text-gray-300 hover:text-white',
          navbar: 'bg-white border-b border-gray-200',
          input: 'border border-gray-300 focus:border-gray-800',
          link: 'text-gray-800 hover:text-gray-700'
        };
      case 'professional':
        return {
          container: 'bg-slate-50',
          card: 'bg-white border border-slate-200 shadow-lg',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-slate-300 text-slate-700 hover:border-slate-400',
          text: 'text-slate-900',
          textSecondary: 'text-slate-600',
          accent: 'text-blue-600',
          section: 'bg-white',
          hero: 'bg-slate-50',
          footer: 'bg-blue-900 text-white',
          footerText: 'text-white',
          footerTextSecondary: 'text-blue-100',
          footerAccent: 'text-blue-200',
          footerLink: 'text-blue-100 hover:text-white',
          navbar: 'bg-white border-b border-slate-200',
          input: 'border border-slate-300 focus:border-blue-500',
          link: 'text-blue-600 hover:text-blue-700'
        };
      case 'clean':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-100 shadow-sm',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-gray-500',
          section: 'bg-gray-50',
          hero: 'bg-white',
          footer: 'bg-gray-100 border-t border-gray-200',
          navbar: 'bg-white border-b border-gray-100',
          input: 'border border-gray-200 focus:border-gray-500',
          link: 'text-gray-700 hover:text-gray-900'
        };
      case 'landing':
        return {
          container: 'bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800',
          card: 'bg-white/10 backdrop-blur-md border border-white/20',
          button: 'bg-white text-blue-700 hover:bg-blue-50 font-semibold shadow-lg',
          buttonSecondary: 'border-2 border-white text-white hover:bg-white hover:text-blue-700 font-semibold',
          text: 'text-white',
          textSecondary: 'text-white/90',
          accent: 'text-white/70',
          section: 'bg-white/50',
          hero: 'bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800',
          footer: 'bg-gradient-to-r from-blue-800 to-purple-800 text-white',
          navbar: 'bg-blue-600/95 backdrop-blur-md border-b border-white/20',
          input: 'bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-white',
          link: 'text-white hover:text-white/80'
        };
      case 'elegant':
        return {
          container: 'bg-gradient-to-br from-blue-50 via-white to-blue-100',
          card: 'bg-white/80 backdrop-blur-sm border border-blue-200/50 shadow-sm',
          button: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white',
          buttonSecondary: 'border-2 border-blue-600 text-blue-800 hover:border-blue-700 hover:bg-blue-50',
          text: 'text-blue-900',
          textSecondary: 'text-blue-600',
          accent: 'text-blue-500',
          section: 'bg-white/50',
          hero: 'bg-gradient-to-br from-blue-50 via-white to-blue-100',
          footer: 'bg-gradient-to-r from-blue-800 to-blue-900 text-white',
          footerText: 'text-white',
          footerTextSecondary: 'text-blue-100',
          footerAccent: 'text-blue-200',
          footerLink: 'text-blue-100 hover:text-white',
          navbar: 'bg-white/80 backdrop-blur-sm border-b border-blue-200/50',
          input: 'border border-blue-300 focus:border-blue-500',
          link: 'text-blue-300 hover:text-white'
        };
      case 'dark':
        return {
          container: 'bg-gray-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10',
          button: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700',
          buttonSecondary: 'border-2 border-white/30 text-white hover:bg-white/10',
          text: 'text-white',
          textSecondary: 'text-gray-300',
          accent: 'text-purple-200',
          section: 'bg-gray-800/50',
          hero: 'bg-gray-900',
          footer: 'bg-black text-white',
          footerText: 'text-white',
          footerTextSecondary: 'text-gray-300',
          footerAccent: 'text-purple-200',
          footerLink: 'text-purple-200 hover:text-white',
          navbar: 'bg-gray-900/95 backdrop-blur-md border-b border-white/10',
          input: 'bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-purple-400',
          link: 'text-purple-200 hover:text-white'
        };
      default:
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-blue-600',
          section: 'bg-gray-50',
          hero: 'bg-white',
          footer: 'bg-blue-900 text-white',
          footerText: 'text-white',
          footerTextSecondary: 'text-blue-100',
          footerAccent: 'text-blue-200',
          footerLink: 'text-blue-100 hover:text-white',
          navbar: 'bg-white border-b border-gray-200',
          input: 'border border-gray-300 focus:border-blue-500',
          link: 'text-blue-600 hover:text-blue-700'
        };
    }
  };

  const value = {
    variant: currentVariant,
    styles: getVariantStyles(),
    isDark: ['modern', 'dark'].includes(currentVariant),
    isGradient: ['modern', 'creative', 'landing', 'elegant'].includes(currentVariant),
    isMinimal: currentVariant === 'minimal',
    isCorporate: currentVariant === 'corporate',
    isCreative: currentVariant === 'creative'
  };

  return (
    <VariantContext.Provider value={value}>
      {children}
    </VariantContext.Provider>
  );
};
